import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ColumnTogglePanel,
  ToggleableColumn,
} from '../components/ColumnToggle/ColumnTogglePanel'
import { TableConfig } from '../features/ui-config'
import { SupportedLanguage, fallbackLanguage } from '../utils/i18n'

const getStoredFields = (storageKey: string): string[] | null => {
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored !== null) {
      const parsed = JSON.parse(stored) as unknown
      if (Array.isArray(parsed)) return parsed as string[]
    }
  } catch {
    // ignore storage errors
  }
  return null
}

const saveFields = (storageKey: string, fields: string[]): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(fields))
  } catch {
    // ignore storage errors
  }
}

interface UseColumnToggleResult {
  visibleFields: string[]
  effectiveTableConfig: TableConfig
  navigationColumnHeader: React.ReactNode
  show: (field: string) => boolean
}

export function useColumnToggle(
  tableConfig: TableConfig,
  toggleableFields: string[],
  storageKey: string,
  defaultAllVisible: boolean = false,
): UseColumnToggleResult {
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language

  const toggleableColumns = useMemo(
    () =>
      tableConfig.columns
        .filter(col => {
          const field = col.filterField ?? col.field
          return field && toggleableFields.includes(field)
        })
        .map(col => ({
          field: (col.filterField ?? col.field)!,
          label:
            col.header?.[currentLanguage as SupportedLanguage] ??
            col.header?.[fallbackLanguage] ??
            '',
        })),
    [tableConfig.columns, toggleableFields, currentLanguage],
  )

  const [visibleFields, setVisibleFields] = useState<string[]>(
    () =>
      getStoredFields(storageKey) ??
      (defaultAllVisible ? [...toggleableFields] : []),
  )
  const [pendingFields, setPendingFields] = useState<string[]>(
    () =>
      getStoredFields(storageKey) ??
      (defaultAllVisible ? [...toggleableFields] : []),
  )

  const effectiveTableConfig = useMemo(
    () => ({
      ...tableConfig,
      columns: tableConfig.columns.filter(col => {
        const field = col.filterField ?? col.field
        return (
          !field ||
          !toggleableFields.includes(field) ||
          visibleFields.includes(field)
        )
      }),
    }),
    [tableConfig, toggleableFields, visibleFields],
  )

  const handleConfirm = (fields: string[]) => {
    setVisibleFields(fields)
    saveFields(storageKey, fields)
  }

  const show = (field: string) =>
    !toggleableFields.includes(field) || visibleFields.includes(field)

  const navigationColumnHeader = (
    <ColumnTogglePanel
      toggleableColumns={toggleableColumns}
      visibleFields={visibleFields}
      pendingFields={pendingFields}
      setPendingFields={setPendingFields}
      onConfirm={handleConfirm}
    />
  )

  return { visibleFields, effectiveTableConfig, navigationColumnHeader, show }
}

interface UseColumnToggleDirectResult {
  visibleFields: string[]
  show: (field: string) => boolean
  navigationColumnHeader: React.ReactNode
}

export function useColumnToggleDirect(
  toggleableColumns: ToggleableColumn[],
  storageKey: string,
  defaultAllVisible: boolean = false,
): UseColumnToggleDirectResult {
  const [visibleFields, setVisibleFields] = useState<string[]>(() => {
    const stored = getStoredFields(storageKey)
    return (
      stored ?? (defaultAllVisible ? toggleableColumns.map(c => c.field) : [])
    )
  })
  const [pendingFields, setPendingFields] = useState<string[]>(() => {
    const stored = getStoredFields(storageKey)
    return (
      stored ?? (defaultAllVisible ? toggleableColumns.map(c => c.field) : [])
    )
  })

  const handleConfirm = (fields: string[]) => {
    setVisibleFields(fields)
    saveFields(storageKey, fields)
  }

  const show = (field: string) =>
    !toggleableColumns.some(c => c.field === field) ||
    visibleFields.includes(field)

  const navigationColumnHeader = (
    <ColumnTogglePanel
      toggleableColumns={toggleableColumns}
      visibleFields={visibleFields}
      pendingFields={pendingFields}
      setPendingFields={setPendingFields}
      onConfirm={handleConfirm}
    />
  )

  return { visibleFields, show, navigationColumnHeader }
}
